import { sendGAEvent } from '@noths/polaris-client-google-analytics';
import getConfig from 'next/config';

import { TaxonomySoftNavPageEvent } from 'src/featureFlags/featureFlags';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { PageEventObject } from 'src/tracking/events/pageEvent';
import { buildPageEventObject } from 'src/tracking/events/pageEvent';
import { getFilterNavType } from 'src/tracking/utils/getFilterNavType';
import type { NextConfig } from 'src/types/nextConfig';
import { logger } from 'src/utils/serverLogger';

/**
 * Event name GTM triggers on for a taxonomy soft navigation.
 *
 * Deliberately distinct from anything already in the data layer so it cannot
 * collide with an existing trigger. Agree it with analytics before enabling the
 * flag: if GTM already fires a page view on History Change then the refreshed
 * `page` object below is the only part that matters and this name goes unused,
 * and if it does not then GTM needs a trigger built on this name.
 */
export const TAXONOMY_PAGE_VIEW_EVENT = 'taxonomy_page_view';

export type TaxonomyPageViewEvent = PageEventObject & {
  event: typeof TAXONOMY_PAGE_VIEW_EVENT;
};

export const isTaxonomySoftNavPageEventEnabled = (state: ReduxApplicationState): boolean =>
  state.user.featureFlags?.[TaxonomySoftNavPageEvent.NAME] === TaxonomySoftNavPageEvent.ON;

/**
 * Refreshes the data layer `page` object after a soft navigation to a different
 * taxonomy listing.
 *
 * The server-rendered object is written once by an inline script and cannot
 * re-execute, so without this every event for the rest of the session carries
 * the entry page's `list_id`, `total_listing_items`, `attribution_token` and
 * `returned_products`. See DISC-4541.
 *
 * Not deferred, unlike the filter events. This has to reach the data layer
 * before any impression for the newly rendered products, or those impressions
 * are attributed to the page the customer just left. Calling it from a Redux
 * event handler means the products are in state but React has not re-rendered
 * yet, which is exactly the window we want.
 */
export const sendTaxonomyPageViewEvent = (state: ReduxApplicationState) => {
  const { publicRuntimeConfig } = getConfig() as NextConfig;

  let pageEventObject: PageEventObject;

  try {
    pageEventObject = buildPageEventObject(
      publicRuntimeConfig.version,
      publicRuntimeConfig.appVersion,
      state,
      { filterNavType: getFilterNavType() },
    );
  } catch (error) {
    // A page object we cannot build is worse than none: it would overwrite the
    // entry page's values with blanks. Leave the data layer as it is.
    logger.warn({
      location: 'sendTaxonomyPageViewEvent',
      extra: { error },
    });

    return;
  }

  sendGAEvent<TaxonomyPageViewEvent>({
    event: TAXONOMY_PAGE_VIEW_EVENT,
    ...pageEventObject,
  });
};
